package com.islamicmedia.site.components

import androidx.compose.runtime.*
import com.islamicmedia.shared.AppConfig
import com.islamicmedia.shared.domain.model.DailyLimitNotice
import com.islamicmedia.shared.domain.model.Entitlement
import com.islamicmedia.shared.domain.model.PricingConfig
import com.islamicmedia.shared.domain.model.PricingPlan
import com.islamicmedia.shared.platform.Event
import com.islamicmedia.shared.platform.Funnel
import com.islamicmedia.shared.platform.FunnelStep
import com.islamicmedia.shared.presentation.ReferralCopy
import com.islamicmedia.shared.util.withoutTrailingYear
import com.islamicmedia.site.theme.*
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Full-screen blocking paywall modal. Prices come from [plans] (admin-controlled, GET /api/pricing),
 * defaulted to the built-in catalogue. See PRD_SUBSCRIPTION_BILLING.md.
 *
 * Two layouts, matching mobile's [PaywallOverlay] exactly:
 *  - **New style** (signed-in first-time subscriber, behind [AppConfig.PAYWALL_NEW_STYLE]) — the
 *    Fitrah_Filter_User_Flow.pdf "SCREEN B" timeline, then the plan choice, then one CTA.
 *  - **Comparison columns** (plan switching) — plans side-by-side, each with its own CTA.
 */
@Composable
fun PaywallOverlay(
    title: String?,
    entitlement: Entitlement?,
    isAuthenticated: Boolean,
    onSubscribe: (planKey: String) -> Unit,
    onSignIn: () -> Unit,
    onDismiss: () -> Unit,
    loading: Boolean = false,
    currentPlan: String? = null,
    onSwitchPlan: (planKey: String) -> Unit = {},
    plans: List<PricingPlan> = PricingConfig.DEFAULT.plans,
    /** Set when the admin has switched the paywall to the ThriveCart promotion — the fully-formed
     *  checkout URL, buyer details already pre-filled. Null keeps the normal Stripe paywall.
     *  Mirrors composeApp's PaywallOverlay. */
    thriveCartUrl: String? = null,
    /** Opens the "Have a voucher?" modal. Null hides the link entirely — used by the plan
     *  switcher and "get credits", where the caller is already a member. */
    onHaveVoucher: (() -> Unit)? = null,
    /** Opens the "Have a referral code?" dialog. Null hides the link — the caller passes it only
     *  to a signed-in non-subscriber with no code attached yet, while the program is on. */
    onHaveReferralCode: (() -> Unit)? = null,
    /** The friend's code already attached to this account (MediaCheckerUiState.referralAttachedCode).
     *  Non-null shows the "Referral applied" chip in place of the link. */
    referralAppliedCode: String? = null,
    /** Fired when the embedded checkout reaches its success page. Mirrors composeApp's twin. */
    onCheckoutSucceeded: () -> Unit = {},
    /** Fired when the success panel's countdown ends. NOT [onDismiss] — paying isn't giving up. */
    onCheckoutSuccessDismissed: () -> Unit = {},
) {
    val switchMode = currentPlan != null

    // ThriveCart promotion mode: that page IS the paywall (its own pitch, pricing and card form),
    // embedded rather than linked so the buyer never leaves the site mid-purchase. Never in switch
    // mode — an existing subscriber changing plans belongs in the Stripe flow.
    if (thriveCartUrl != null && !switchMode) {
        ThriveCartCheckoutFrame(
            url = thriveCartUrl,
            title = title,
            onDismiss = onDismiss,
            onSucceeded = onCheckoutSucceeded,
            onSuccessDismissed = onCheckoutSuccessDismissed,
        )
        return
    }
    val useNewPaywall = isAuthenticated && AppConfig.PAYWALL_NEW_STYLE && !switchMode

    // Plan the intro CTA buys while the chooser is hidden. Resolved against the SERVER's plan list,
    // and left null when that key isn't in it — then we fall back to showing the chooser rather than
    // sending anyone to Stripe with a key we can't identify. Never applies in switch mode, where the
    // whole point is picking a different plan. Mirrors composeApp's PaywallOverlay.
    val directPlan = plans.firstOrNull { it.key == AppConfig.PAYWALL_DIRECT_PLAN_KEY }
        ?.takeIf { AppConfig.PAYWALL_SKIP_PLAN_STEP && !switchMode }
    // Which column's CTA was clicked, so only that button shows the loading label.
    var pendingKey by remember { mutableStateOf<String?>(null) }
    // New-style paywall step: false = the pitch (timeline), true = "Choose your plan". Splitting
    // them keeps each card short enough to fit without scrolling.
    var showPlanStep by remember { mutableStateOf(false) }

    // ── Funnel instrumentation ────────────────────────────────────────────────────────────────
    // This is the ONLY analytics the web paywall has: shared AnalyticsTracker.js is a no-op, so
    // Firebase never sees a single web user. Everything below reaches the admin funnel instead.
    LaunchedEffect(Unit) {
        Funnel.track(
            Event.PAYWALL_SHOWN, FunnelStep.PAYWALL_SHOWN,
            mapOf("style" to if (useNewPaywall) "two_step" else "classic", "switch_mode" to switchMode.toString())
        )
    }
    LaunchedEffect(showPlanStep) {
        if (showPlanStep) Funnel.track(Event.PAYWALL_PLAN_SHOWN, FunnelStep.PLAN_SHOWN)
    }
    // Wrapped once so the backdrop click and the X can't diverge. begin_checkout is NOT tracked
    // here — MediaCheckerViewModel.startCheckout owns it for both platforms (see mobile's twin).
    val dismissAndTrack: () -> Unit = {
        Funnel.track(Event.PAYWALL_DISMISSED, params = mapOf("step" to if (showPlanStep) "offer" else "intro"))
        onDismiss()
    }

    // Wider card when showing more columns; narrower for 1–2 plans so they don't stretch.
    val cardMaxWidth = when {
        !isAuthenticated -> 380
        useNewPaywall -> 460
        plans.size <= 1 -> 400
        plans.size == 2 -> 600
        else -> 780
    }

    // Full-viewport backdrop — blocks all interaction with the screen behind the paywall.
    Div(attrs = {
        onClick { dismissAndTrack() }
        style {
            property("position", "fixed")
            property("top", "0"); property("left", "0"); property("right", "0"); property("bottom", "0")
            property("background", "rgba(15, 23, 42, 0.55)")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            padding(20.px)
            property("box-sizing", "border-box")
            property("z-index", "1000")
        }
    }) {
        // Paywall card — stop click propagation so taps inside don't dismiss.
        Div(attrs = {
            onClick { event -> event.stopPropagation() }
            style {
                property("position", "relative")
                property("width", "min(94%, ${cardMaxWidth}px)")
                property("max-height", "90vh")
                property("overflow-y", "auto")
                // Explicit, because a lone `overflow-y: auto` makes overflow-x compute to `auto`
                // too — which would give the oversized gift pair a horizontal scrollbar instead of
                // letting it bleed off the card edges.
                property("overflow-x", "hidden")
                backgroundColor(Color.white)
                borderRadius(24.px)
                property("box-shadow", CardElevatedShadow)
                padding(28.px)
                property("text-align", "center")
                property("box-sizing", "border-box")
            }
        }) {
            // Close (X)
            Button(attrs = {
                onClick { dismissAndTrack() }
                style {
                    property("position", "absolute")
                    property("top", "12px"); property("right", "12px")
                    width(32.px); height(32.px)
                    borderRadius(50.percent)
                    property("background", "transparent")
                    property("border", "none")
                    cursor("pointer")
                    fontSize(18.px)
                    color(Gray400)
                }
            }) { Text("✕") }

            if (useNewPaywall) {
                if (!showPlanStep) {
                    PaywallIntroStep(title = title, loading = loading) {
                        Funnel.track(Event.PAYWALL_CONTINUE, FunnelStep.PAYWALL_CONTINUE)
                        if (directPlan != null) {
                            // The plan chooser is hidden, so the offer is presented on Stripe's page
                            // instead. Still count PLAN_SHOWN: the funnel step means "the offer
                            // reached them", and dropping it would read as every user abandoning
                            // at that step.
                            Funnel.track(Event.PAYWALL_PLAN_SHOWN, FunnelStep.PLAN_SHOWN)
                            pendingKey = directPlan.key
                            onSubscribe(directPlan.key)
                        } else {
                            showPlanStep = true
                        }
                    }
                } else {
                    PaywallSpecialOfferStep(
                        plans = plans,
                        loading = loading,
                        onSubscribe = onSubscribe,
                        onBack = {
                            Funnel.track(Event.PAYWALL_BACK)
                            showPlanStep = false
                        }
                    )
                }
            } else {

            Img(src = "/images/unlocked.png", attrs = {
                style { width(48.px); height(48.px); property("margin", "0 auto 12px"); display(DisplayStyle.Block) }
            })

            H3(attrs = {
                style { margin(0.px); fontSize(22.px); fontWeight(700); color(Gray900) }
            }) { Text(if (switchMode) "Change your plan" else "Unlock more analyses") }

            if (title != null) {
                P(attrs = {
                    style { color(Gray500); fontSize(14.px); margin(6.px, 0.px, 0.px) }
                }) { Text("Continue analyzing \"$title\"") }
            }

            if (!isAuthenticated) {
                Div(attrs = { style { marginTop(20.px) } }) {
                    P(attrs = {
                        style { color(Gray600); fontSize(14.px); margin(0.px, 0.px, 16.px) }
                    }) { Text("Create an account to subscribe") }

                    Button(attrs = {
                        onClick { onSignIn() }
                        style {
                            width(100.percent); padding(14.px); fontSize(16.px); fontWeight(700)
                            color(Color.white); property("background", GradientPrimary)
                            borderRadius(14.px); property("border", "none"); cursor("pointer")
                        }
                    }) { Text("Sign Up") }
                }
            } else {
                // ── Comparison columns ──────────────────────────────────────────
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        property("flex-wrap", "wrap")
                        gap(16.px)
                        property("align-items", "stretch")
                        justifyContent(JustifyContent.Center)
                        marginTop(22.px)
                    }
                }) {
                    plans.forEach { plan ->
                        val featured = plan.badge != null
                        val isCurrentCol = switchMode && plan.key == currentPlan
                        val isLoadingThis = loading && pendingKey == plan.key
                        val savePercent = plan.savePercent

                        Div(attrs = {
                            style {
                                property("position", "relative")
                                property("flex", "1 1 200px")
                                property("min-width", "180px")
                                display(DisplayStyle.Flex)
                                property("flex-direction", "column")
                                property("text-align", "left")
                                padding(24.px, 18.px)
                                borderRadius(18.px)
                                if (featured) {
                                    // Gradient border via padding-box/border-box trick + raised shadow.
                                    property("background", "linear-gradient(#fff,#fff) padding-box, ${GradientPrimary} border-box")
                                    property("border", "2px solid transparent")
                                    property("box-shadow", "0 16px 40px -14px rgba(14,124,123,0.4)")
                                } else {
                                    backgroundColor(Color.white)
                                    property("border", "1.5px solid ${Gray200}")
                                }
                            }
                        }) {
                            // ✨ "Best value" pill — centered on the top edge of the featured column.
                            if (plan.badge != null) {
                                Div(attrs = {
                                    style {
                                        property("position", "absolute")
                                        property("top", "-13px"); property("left", "50%")
                                        property("transform", "translateX(-50%)")
                                        property("background", "linear-gradient(90deg, #FFD970, #E0A50E)")
                                        borderRadius(50.px)
                                        padding(5.px, 14.px)
                                        fontSize(11.px); fontWeight(800)
                                        color(Color("#5A3D00"))
                                        property("box-shadow", "0 3px 8px rgba(224,165,14,0.5)")
                                        property("white-space", "nowrap")
                                    }
                                }) { Text("✨ ${plan.badge} ✨") }
                            }

                            // Plan name label
                            Div(attrs = {
                                style {
                                    fontSize(12.px); fontWeight(800)
                                    property("letter-spacing", "0.08em")
                                    property("text-transform", "uppercase")
                                    color(if (featured) Primary else Gray500)
                                }
                            }) { Text(plan.name) }

                            // Old price + derived "Save %" — fixed height so prices line up across columns.
                            Div(attrs = {
                                style {
                                    property("min-height", "20px")
                                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px)
                                    margin(8.px, 0.px, 2.px)
                                }
                            }) {
                                if (plan.oldPrice != null) {
                                    Span(attrs = {
                                        style {
                                            fontSize(13.px); fontWeight(600); color(Gray400)
                                            property("text-decoration", "line-through")
                                        }
                                    }) { Text(plan.oldPrice!!) }
                                }
                            }

                            // Live price
                            Div(attrs = {
                                style { fontSize(26.px); fontWeight(800); color(Primary); property("line-height", "1.1") }
                            }) { Text(plan.price) }

                            // What you get: entitlement summary (unlimited / counts) + marketing bullets
                            Div(attrs = { style { marginTop(16.px); marginBottom(20.px) } }) {
                                plan.displayLines.forEach { feat ->
                                    Div(attrs = {
                                        style {
                                            display(DisplayStyle.Flex)
                                            property("align-items", "flex-start")
                                            gap(8.px)
                                            marginBottom(9.px)
                                        }
                                    }) {
                                        Span(attrs = {
                                            style { color(Primary); fontWeight(800); fontSize(13.px); property("line-height", "1.4") }
                                        }) { Text("✓") }
                                        Span(attrs = {
                                            style { fontSize(13.px); color(Gray600); property("line-height", "1.4") }
                                        }) { Text(feat) }
                                    }
                                }
                            }

                            // CTA — pushed to the bottom so columns of different heights align.
                            val label = when {
                                isLoadingThis -> if (switchMode) "Switching…" else "Starting…"
                                isCurrentCol -> "Current plan"
                                switchMode -> "Switch to ${plan.name}"
                                else -> "Choose ${plan.name}"
                            }
                            Button(attrs = {
                                if (loading || isCurrentCol) attr("disabled", "true")
                                else onClick {
                                    pendingKey = plan.key
                                    if (switchMode) onSwitchPlan(plan.key) else onSubscribe(plan.key)
                                }
                                style {
                                    property("margin-top", "auto")
                                    width(100.percent); padding(12.px)
                                    fontSize(14.px); fontWeight(700)
                                    borderRadius(12.px)
                                    cursor(if (loading || isCurrentCol) "default" else "pointer")
                                    if (featured) {
                                        color(Color.white)
                                        property("background", GradientPrimary)
                                        property("border", "none")
                                    } else {
                                        color(Primary)
                                        backgroundColor(Color.white)
                                        property("border", "1.5px solid rgba(14,124,123,0.35)")
                                    }
                                    if (loading || isCurrentCol) property("opacity", "0.7")
                                }
                            }) { Text(label) }
                        }
                    }
                }

                P(attrs = {
                    style { color(Gray500); fontSize(12.px); margin(16.px, 0.px, 0.px) }
                }) { Text(if (switchMode) "Takes effect today. You only pay the difference." else "You'll finish payment in a new tab.") }
            }
            }

            // "Have a voucher?" · "Have a referral code?" — one quiet line, the two links separated
            // by a dot (owner, 2026-09-25). Outside the new/classic branch so it shows on both,
            // after the plans; never in switch mode. Either link may be absent (no dot then). Once a
            // friend's code is attached, its chip replaces the referral link, below the line.
            // Mirrors mobile.
            if (!switchMode) {
                val voucherLink = onHaveVoucher
                val referralLink = onHaveReferralCode?.takeIf { referralAppliedCode == null }
                if (voucherLink != null || referralLink != null) {
                    Div(attrs = {
                        style {
                            property("margin-top", "14px")
                            property("text-align", "center")
                            fontSize(13.px); fontWeight(600)
                        }
                    }) {
                        if (voucherLink != null) PaywallTextLink("Have a voucher?", voucherLink)
                        if (voucherLink != null && referralLink != null) {
                            Span(attrs = { style { color(Gray400); property("margin", "0 8px") } }) { Text("·") }
                        }
                        if (referralLink != null) PaywallTextLink(ReferralCopy.PAYWALL_LINK, referralLink)
                    }
                }
                if (referralAppliedCode != null) {
                    Div(attrs = { style { property("margin-top", "14px"); property("text-align", "center") } }) {
                        Span(attrs = {
                            style {
                                display(DisplayStyle.InlineBlock)
                                padding(6.px, 12.px)
                                // 12px, not a pill: on a narrow screen the sentence wraps, and a
                                // two-line pill rounds into a lozenge. Same as mobile's chip.
                                borderRadius(12.px)
                                backgroundColor(PrimaryPale)
                                property("border", "1px solid rgba(14,124,123,0.25)")
                                fontSize(12.px); fontWeight(600); color(Primary)
                            }
                        }) { Text("✓ ${ReferralCopy.APPLIED_CHIP}") }
                    }
                }
            }
        }
    }
}

/** One of the paywall's quiet text links ("Have a voucher?", "Have a referral code?"). */
@Composable
private fun PaywallTextLink(label: String, onClick: () -> Unit) {
    Span(attrs = {
        onClick { onClick() }
        style {
            color(Primary)
            property("text-decoration", "underline")
            cursor("pointer")
        }
    }) { Text(label) }
}

// ---------------------------------------------------------------------------
// New-style paywall (web twin of composeApp's PdfPaywall)
// ---------------------------------------------------------------------------

/** One timeline step's icon. Tabler-derived geometry, drawn as an SVG data-URI because the
 *  security hook blocks innerHTML — same approach as ConcernCard's SafetyShieldIcon. */
private fun stepIconPath(kind: String): String = when (kind) {
    "search" -> "M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0 M21 21l-6 -6"
    "star" -> "M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z"
    "family" -> "M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0 M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2 M16 3.13a4 4 0 0 1 0 7.75 M21 21v-2a4 4 0 0 0 -3 -3.85"
    // Material/Tabler arrow-back, for the step-2 back affordance.
    "arrow-back" -> "M5 12l14 0 M5 12l6 6 M5 12l6 -6"
    else -> "M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z M12 16m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0 M8 11v-4a4 4 0 0 1 8 0"
}

@Composable
private fun StepIcon(kind: String, filled: Boolean) {
    // Teal on pale teal, or white on solid teal for the emphasized final step.
    val hex = if (filled) "ffffff" else "0E7C7B"
    val svg = "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' " +
        "fill='none' stroke='%23" + hex + "' stroke-width='2' stroke-linecap='round' " +
        "stroke-linejoin='round'><path d='" + stepIconPath(kind) + "'/></svg>"
    Div(attrs = {
        style {
            width(36.px); height(36.px); borderRadius(50.percent)
            property("flex-shrink", "0")
            backgroundColor(if (filled) Primary else PrimaryPale)
            property("background-image", "url(\"data:image/svg+xml,$svg\")")
            property("background-repeat", "no-repeat")
            property("background-position", "center")
            property("background-size", "18px 18px")
        }
    })
}

/** One timeline step: circled icon with an amber "done" tick, a connector line down to the next
 *  step, and the heading/description beside it. Mirrors composeApp's FlowStep. */
@Composable
/** [heading] is a content slot, not a String, so a step can strike through part of its heading. */
private fun FlowStep(
    kind: String,
    filled: Boolean,
    heading: @Composable () -> Unit,
    desc: String,
    showConnector: Boolean
) {
    Div(attrs = {
        style { display(DisplayStyle.Flex); property("align-items", "stretch"); property("text-align", "left") }
    }) {
        Div(attrs = {
            style { display(DisplayStyle.Flex); property("flex-direction", "column"); alignItems(AlignItems.Center) }
        }) {
            Div(attrs = { style { property("position", "relative") } }) {
                StepIcon(kind, filled)
                // Amber tick on the icon's bottom-right.
                Div(attrs = {
                    style {
                        property("position", "absolute"); property("right", "-2px"); property("bottom", "-2px")
                        width(16.px); height(16.px); borderRadius(50.percent)
                        backgroundColor(ScoreAmber); property("border", "1.5px solid white")
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                        fontSize(9.px); color(Color.white); fontWeight(800); property("line-height", "1")
                    }
                }) { Text("✓") }
            }
            if (showConnector) {
                Div(attrs = { style { width(2.px); property("flex", "1"); backgroundColor(Gray200) } })
            }
        }
        Div(attrs = {
            style { property("margin-left", "14px"); property("padding-bottom", if (showConnector) "20px" else "0") }
        }) {
            Div(attrs = { style { fontSize(15.px); fontWeight(700); color(Gray900) } }) { heading() }
            Div(attrs = {
                style { fontSize(13.px); color(Gray600); property("line-height", "1.4"); property("margin-top", "2px") }
            }) { Text(desc) }
        }
    }
}

/** Small trust-signal row shown above the intro step's CTA ("Let's get started, bismillah!"):
 *  five filled stars, the score, and what it's based on. Update the score/caption by hand when
 *  the underlying store rating changes — there's no live API for either app store's rating. */
@Composable
private fun RatingBadge() {
    val starSvg = "<svg xmlns='http://www.w3.org/2000/svg' width='13' height='13' viewBox='0 0 24 24' " +
        "fill='%23E0A50E' stroke='none'><path d='" + stepIconPath("star") + "'/></svg>"
    Div(attrs = {
        style {
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            gap(2.px); property("margin-top", "18px")
        }
    }) {
        repeat(5) {
            Div(attrs = {
                style {
                    width(13.px); height(13.px)
                    property("background-image", "url(\"data:image/svg+xml,$starSvg\")")
                    property("background-repeat", "no-repeat")
                    property("background-size", "13px 13px")
                }
            })
        }
        Span(attrs = {
            style { fontSize(12.px); fontWeight(700); color(Gray700); property("margin-left", "4px") }
        }) { Text("5.0") }
        Span(attrs = {
            style { fontSize(12.px); color(Gray500); property("margin-left", "4px") }
        }) { Text("(based on iOS reviews)") }
    }
}

@Composable
private fun PaywallIntroStep(title: String?, loading: Boolean = false, onContinue: () -> Unit) {
    Div(attrs = { style { property("text-align", "left") } }) {
        H3(attrs = {
            style { margin(0.px); fontSize(22.px); fontWeight(700); color(Gray900) }
        }) { Text("Unlock more searches") }
        P(attrs = {
            style { color(Gray600); fontSize(14.px); margin(8.px, 0.px, 0.px); property("line-height", "1.45") }
        }) { Text("Access all concerns, highlights, alternatives, and recommendations.") }
        if (title != null) {
            P(attrs = {
                style { color(Gray500); fontSize(13.px); margin(6.px, 0.px, 0.px) }
            }) { Text("Continue analyzing \"$title\"") }
        }

        // Timeline card
        Div(attrs = {
            style {
                property("margin-top", "20px"); padding(18.px); borderRadius(20.px)
                backgroundColor(Color.white)
                property("box-shadow", "0 4px 16px -6px rgba(0,0,0,0.18)")
                property("border", "1px solid ${Gray200}")
                property("box-sizing", "border-box")
            }
        }) {
            FlowStep("search", false, { Text("Quick check") },
                "An instant verdict in about 60 seconds. Fast and reliable every time.", true)
            FlowStep("star", false, { Text("The full picture") },
                "Detailed explanations of each Islamic and developmental concern, plus severity, exact age guidance, what to skip, and safer picks.", true)
            FlowStep("family", false, { Text("Tailored to your family") },
                "Add any personal concerns you'd like flagged plus your kids' ages for more tailored results.", true)
            FlowStep(
                "unlock", true,
                heading = {
                    Text("Unlock everything for ")
                    Span(attrs = {
                        style { property("text-decoration", "line-through"); color(Gray400) }
                    }) { Text("\u002429") }
                    Text(" \u00249")
                },
                desc = "Click below to get started.", showConnector = false
            )
        }

        RatingBadge()
        Button(attrs = {
            // This CTA can now open Stripe directly, so a second click while the session is being
            // created would start a duplicate checkout.
            if (loading) disabled() else onClick { onContinue() }
            style {
                property("margin-top", "20px")
                width(100.percent); padding(14.px)
                fontSize(16.px); fontWeight(600)
                color(Color.white); property("background", GradientPrimary)
                borderRadius(14.px); property("border", "none")
                cursor(if (loading) "default" else "pointer")
                property("box-shadow", "0 6px 18px -8px rgba(14,124,123,0.7)")
                if (loading) opacity(0.7)
            }
        }) { Text(if (loading) "Just a moment…" else "Let's get started, bismillah!") }

        P(attrs = {
            style { color(Gray500); fontSize(12.px); margin(10.px, 0.px, 0.px); property("text-align", "center") }
        }) { Text("No risk \u00B7 Cancel anytime") }
    }
}

/** How long the "act now" countdown runs before it softens into the hold message. */
private const val OFFER_COUNTDOWN_SECONDS = 120


private fun formatCountdown(total: Int): String {
    val m = total / 60
    val s = total % 60
    return "$m:${s.toString().padStart(2, '0')}"
}

/**
 * Step 2 of the new paywall: a one-shot special offer on the ANNUAL plan under a 2-minute
 * countdown, with an opt-out onto the cheap starter plan.
 *
 * The countdown never revokes anything — at zero it swaps to a warm "we're holding it for you"
 * note and both CTAs keep working. It resets whenever this step is entered, so it's urgency
 * framing rather than real scarcity.
 */
@Composable
private fun PaywallSpecialOfferStep(
    plans: List<PricingPlan>,
    loading: Boolean,
    onSubscribe: (planKey: String) -> Unit,
    onBack: () -> Unit
) {
    val annualPlan = plans.firstOrNull { it.key.contains("annual", ignoreCase = true) } ?: plans.firstOrNull()
    val starterPlan = plans.firstOrNull { it.key != annualPlan?.key } ?: annualPlan

    var secondsLeft by remember { mutableStateOf(OFFER_COUNTDOWN_SECONDS) }
    LaunchedEffect(Unit) {
        while (secondsLeft > 0) {
            kotlinx.coroutines.delay(1000)
            secondsLeft -= 1
        }
    }
    val expired = secondsLeft <= 0

    Div(attrs = { style { property("text-align", "center") } }) {
        // Real arrow glyph (SVG data-URI, matching mobile's Icons.AutoMirrored.Filled.ArrowBack)
        // rather than a literal "←" character, whose weight varies by platform font.
        // Absolutely positioned against the card (which is position: relative) using the SAME
        // 12px inset and 32px box as the close X, so the two sit equidistant from the top and
        // their respective sides. Floating it in the content flow put it at the card's 28px
        // padding instead, visibly lower and further in than the X.
        Div(attrs = {
            onClick { onBack() }
            style {
                property("position", "absolute")
                property("top", "12px"); property("left", "12px")
                width(32.px); height(32.px)
                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                cursor("pointer")
            }
        }) {
            Span(attrs = {
                style {
                    property("display", "inline-block")
                    width(16.px); height(16.px); property("flex-shrink", "0")
                    property("background-image", "url(\"data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23737373' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='${stepIconPath("arrow-back")}'/></svg>\")")
                    property("background-repeat", "no-repeat")
                    property("background-position", "center")
                    property("background-size", "20px 20px")
                }
            })
        }

        // One-shot celebration on arrival at this step, matching mobile's ConfettiKit burst.
        ConfettiBurst()

        // Gold "special offer" ribbon. The gift Lottie that used to flank it is gone.
        Div(attrs = {
            style {
                property("display", "inline-block")
                property("background", "linear-gradient(90deg, #FFD970, #E0A50E)")
                // Padding grown with the type so the pill keeps its ribbon proportions at 26px.
                borderRadius(50.px); padding(8.px, 22.px)
                property("margin-top", "6px")
                fontSize(26.px); fontWeight(700); color(Color("#5A3D00"))
                property("box-shadow", "0 3px 10px rgba(224,165,14,0.45)")
                property("white-space", "nowrap")
            }
        }) { Text("SPECIAL OFFER") }

        // The hold message keeps this slot above the card; the live countdown now sits in the
        // plan card's own corner, so nothing occupies this space until the timer runs out.
        if (expired) {
            Div(attrs = {
                style {
                    backgroundColor(PrimaryPale); borderRadius(12.px)
                    padding(12.px, 16.px); property("margin-top", "14px")
                }
            }) {
                Div(attrs = {
                    style { fontSize(15.px); fontWeight(700); color(Primary) }
                }) { Text("Are you still there?") }
                Div(attrs = {
                    style { fontSize(13.px); color(Gray600); property("margin-top", "4px"); property("line-height", "1.4") }
                }) { Text("We're keeping this one time offer open a little longer, just for you") }
            }
        }

        // Hero card for the annual plan.
        annualPlan?.let { plan ->
            Div(attrs = {
                style {
                    property("position", "relative")
                    property("margin-top", "18px"); padding(18.px)
                    borderRadius(18.px); backgroundColor(Color.white)
                    property("border", "2px solid ${Primary}")
                    property("box-shadow", "0 10px 28px -12px rgba(14,124,123,0.55)")
                    property("box-sizing", "border-box")
                }
            }) {
                // Live countdown, parked in the card's top-right corner at half its old size.
                // Hidden once expired — the hold message above the card takes over from here.
                if (!expired) {
                    Div(attrs = {
                        style {
                            property("position", "absolute")
                            property("top", "10px"); property("right", "10px")
                            backgroundColor(ScoreRedBg); property("border", "1px solid ${ScoreRed}")
                            borderRadius(8.px); padding(4.px, 10.px)
                            fontSize(15.px); fontWeight(700); color(ScoreRed)
                            property("font-variant-numeric", "tabular-nums")
                        }
                    }) { Text(formatCountdown(secondsLeft)) }
                }
                Div(attrs = {
                    style {
                        fontSize(12.px); fontWeight(800); color(Primary)
                        property("letter-spacing", "0.08em"); property("text-transform", "uppercase")
                    }
                }) { Text(plan.name) }
                plan.badge?.let { badge ->
                    Div(attrs = {
                        style { fontSize(13.px); fontWeight(700); color(Color("#B45309")); property("margin-top", "8px") }
                    }) { Text(badge) }
                }
                Div(attrs = {
                    style { fontSize(30.px); fontWeight(800); color(Gray900); property("margin-top", "10px") }
                }) { Text(plan.price) }
                // The comparison price reads as context under the headline number rather than a
                // struck-through figure beside it.
                plan.oldPrice?.let { old ->
                    Div(attrs = {
                        style { fontSize(12.px); color(Gray500); property("margin-top", "4px") }
                    }) { Text("(vs $old annually if paid monthly)") }
                }
                // Offer-specific copy, written HERE rather than in PricingPlan because it is only
                // true of this annual offer — the shared model still feeds every other surface.
                // NOTE: this deliberately ignores plan.features, so admin edits to the Annual
                // plan's bullet list do not show on this card.
                val offerLines = listOfNotNull(
                    "Search worry-free for an entire year",
                    plan.perMonthFromAnnual?.let { "$it/month billed annually" } ?: "Billed annually"
                )
                Div(attrs = { style { property("margin-top", "12px") } }) {
                    offerLines.forEach { line ->
                        Div(attrs = {
                            style {
                                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                gap(8.px); property("margin-bottom", "6px")
                            }
                        }) {
                            Span(attrs = { style { color(Primary); fontWeight(800); fontSize(13.px) } }) { Text("✓") }
                            Span(attrs = { style { fontSize(13.px); color(Gray700) } }) { Text(line) }
                        }
                    }
                }
            }
        }

        Div(attrs = {
            style {
                property("margin-top", "14px")
                fontSize(14.px); fontWeight(700); color(ScoreRed)
            }
        }) { Text("This offer won't be available again!") }

        Button(attrs = {
            if (loading || annualPlan == null) attr("disabled", "true")
            else onClick { annualPlan?.let { onSubscribe(it.key) } }
            style {
                property("margin-top", "14px")
                width(100.percent); padding(14.px)
                fontSize(16.px); fontWeight(700)
                color(Color.white); property("background", GradientPrimary)
                borderRadius(14.px); property("border", "none")
                cursor(if (loading) "default" else "pointer")
                property("box-shadow", "0 8px 22px -8px rgba(14,124,123,0.75)")
                if (loading) property("opacity", "0.7")
            }
        }) { Text(if (loading) "Starting…" else "Yes! Claim this offer") }

        // Opt-out onto the starter plan. Deliberately quieter than the CTA above.
        Div(attrs = {
            if (!loading && starterPlan != null) onClick { starterPlan?.let { onSubscribe(it.key) } }
            style {
                property("margin-top", "12px")
                fontSize(14.px); color(Gray600)
                property("text-decoration", "underline")
                cursor(if (loading) "default" else "pointer")
            }
        }) {
            Text("No thanks, I'll pay $9 today")
            Br()
            Text("and $29 each month after")
        }

        Div(attrs = {
            style { color(Gray500); fontSize(12.px); property("margin-top", "10px") }
        }) { Text("You'll finish payment in a new tab.") }
    }
}

/** Human copy for a redemption verdict. Every branch is a distinct, actionable message —
 *  collapsing them into "invalid code" is what makes people email support.
 *  Mirrors the mobile voucherErrorText — keep the two in step. */
private fun voucherErrorText(status: String): String = when (status) {
    "not_found" -> "That code isn't valid. Check for typos and try again."
    "disabled" -> "That code is no longer active."
    "expired" -> "That code has expired."
    "already_redeemed" -> "You've already used this code."
    "already_subscribed" -> "You already have an active subscription, no code needed."
    "rate_limited" -> "Too many attempts. Please try again later."
    else -> "Something went wrong. Please try again."
}

/**
 * "Have a voucher?" code entry. Its own scrim + card ABOVE the paywall, so dismissing returns the
 * user to the plans they were already looking at.
 *
 * Errors stay INSIDE the modal: the user is mid-typo, and closing on failure would make them
 * reopen and retype. Only success closes it.
 *
 * Mirrors the mobile VoucherDialog — keep the two in step.
 */
@Composable
fun VoucherDialog(
    loading: Boolean,
    error: String?,
    onSubmit: (String) -> Unit,
    onDismiss: () -> Unit
) {
    var code by remember { mutableStateOf("") }
    Div(attrs = {
        // Not mid-request, like Cancel below (and the mobile Dialog).
        onClick { if (!loading) onDismiss() }
        style {
            position(Position.Fixed); top(0.px); left(0.px); right(0.px); bottom(0.px)
            backgroundColor(rgba(0, 0, 0, 0.55))
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            // Above the paywall's own scrim.
            property("z-index", "3000")
            padding(16.px)
        }
    }) {
        Div(attrs = {
            // Swallow clicks so tapping the card doesn't hit the dismiss scrim behind it.
            onClick { it.stopPropagation() }
            style {
                width(100.percent); property("max-width", "380px")
                backgroundColor(Color.white); borderRadius(20.px); padding(24.px)
                property("box-sizing", "border-box")
                property("box-shadow", "0 20px 60px -20px rgba(0,0,0,0.45)")
                property("text-align", "center")
            }
        }) {
            Div(attrs = { style { fontSize(32.px) } }) { Text("🎟️") }
            H3(attrs = {
                style { margin(10.px, 0.px, 0.px); fontSize(18.px); fontWeight(700); color(Gray900) }
            }) { Text("Enter your voucher") }
            P(attrs = {
                style { margin(6.px, 0.px, 0.px); fontSize(13.px); color(Gray600) }
            }) { Text("Redeem a code to unlock full access.") }

            Input(InputType.Text, attrs = {
                value(code)
                // Uppercased as they type so what's on screen matches what's stored, and
                // "7free26" can't look like a different code than the one they were sent.
                onInput { code = it.value.uppercase().trim() }
                onKeyDown { if (it.key == "Enter" && !loading && code.isNotBlank()) onSubmit(code) }
                if (loading) disabled()
                // NEVER put a real code here — this placeholder is shown to every user who opens the
                // modal, so an example doubles as a free handout of a live voucher.
                placeholder("Enter your code")
                style {
                    property("margin-top", "18px")
                    width(100.percent); padding(12.px, 14.px)
                    property("box-sizing", "border-box")
                    fontSize(15.px); borderRadius(12.px)
                    property("border", if (error != null) "1.5px solid $ScoreRed" else "1.5px solid $Gray200")
                    property("outline", "none")
                    property("text-align", "center")
                    property("letter-spacing", "1px")
                }
            })

            if (error != null) {
                P(attrs = {
                    style { margin(8.px, 0.px, 0.px); fontSize(12.px); color(ScoreRed); property("line-height", "1.35") }
                }) { Text(voucherErrorText(error)) }
            }

            Button(attrs = {
                onClick { if (!loading && code.isNotBlank()) onSubmit(code) }
                if (loading || code.isBlank()) disabled()
                style {
                    property("margin-top", "16px")
                    width(100.percent); padding(13.px)
                    fontSize(15.px); fontWeight(700)
                    color(Color.white); backgroundColor(Primary)
                    borderRadius(14.px); property("border", "none"); cursor("pointer")
                    if (loading || code.isBlank()) property("opacity", "0.6")
                }
            }) { Text(if (loading) "Redeeming…" else "Redeem") }

            Div(attrs = {
                onClick { if (!loading) onDismiss() }
                style {
                    property("margin-top", "10px"); fontSize(13.px); color(Gray500); cursor("pointer")
                }
            }) { Text("Cancel") }
        }
    }
}

/**
 * "Have a referral code?" entry: a friend's code, attached to this account so the checkout that
 * follows gives them their second month free (their next bill moves back a month) as soon as their
 * first invoice is paid. Cloned from [VoucherDialog]
 * (its own scrim + card above the paywall; errors stay inside, only success closes it). Every word
 * is [ReferralCopy], and [error] is a verdict for [ReferralCopy.errorText].
 *
 * Mirrors the mobile ReferralCodeDialog — keep the two in step.
 *
 * The Free Gift screen's "Got a code from a friend?" (POST /api/referral/redeem) reuses this same
 * look with [title] = ReferralCopy.REDEEM_TITLE and [initialCode] set to any code already attached
 * (ReferralInfo.redeemCode), so a member who attached one at the paywall can just click Apply.
 */
@Composable
fun ReferralCodeDialog(
    loading: Boolean,
    error: String?,
    onSubmit: (String) -> Unit,
    onDismiss: () -> Unit,
    title: String = ReferralCopy.DIALOG_TITLE,
    initialCode: String = "",
) {
    var code by remember { mutableStateOf(initialCode) }
    Div(attrs = {
        onClick { if (!loading) onDismiss() }
        style {
            position(Position.Fixed); top(0.px); left(0.px); right(0.px); bottom(0.px)
            backgroundColor(rgba(0, 0, 0, 0.55))
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            // Above the paywall's own scrim.
            property("z-index", "3000")
            padding(16.px)
        }
    }) {
        Div(attrs = {
            // Swallow clicks so tapping the card doesn't hit the dismiss scrim behind it.
            onClick { it.stopPropagation() }
            style {
                width(100.percent); property("max-width", "380px")
                backgroundColor(Color.white); borderRadius(20.px); padding(24.px)
                property("box-sizing", "border-box")
                property("box-shadow", "0 20px 60px -20px rgba(0,0,0,0.45)")
                property("text-align", "center")
            }
        }) {
            Div(attrs = { style { fontSize(32.px) } }) { Text("🎁") }
            H3(attrs = {
                style { margin(10.px, 0.px, 0.px); fontSize(18.px); fontWeight(700); color(Gray900) }
            }) { Text(title) }

            Input(InputType.Text, attrs = {
                value(code)
                // Not uppercased or trimmed here, unlike the voucher field: the ViewModel
                // normalises (case, spaces, the dash, O/I/L look-alikes) and also accepts a pasted
                // share link or message, which uppercasing would mangle on screen. Mirrors mobile.
                onInput { code = it.value }
                onKeyDown { if (it.key == "Enter" && !loading && code.isNotBlank()) onSubmit(code) }
                if (loading) disabled()
                placeholder(ReferralCopy.DIALOG_PLACEHOLDER)
                attr("autocomplete", "off")
                attr("autocapitalize", "characters")
                attr("spellcheck", "false")
                style {
                    property("margin-top", "18px")
                    width(100.percent); padding(12.px, 14.px)
                    property("box-sizing", "border-box")
                    fontSize(15.px); borderRadius(12.px)
                    property("border", if (error != null) "1.5px solid $ScoreRed" else "1.5px solid $Gray200")
                    property("outline", "none")
                    property("text-align", "center")
                    property("letter-spacing", "1px")
                }
            })

            ReferralCopy.errorText(error)?.let { message ->
                P(attrs = {
                    style { margin(8.px, 0.px, 0.px); fontSize(12.px); color(ScoreRed); property("line-height", "1.35") }
                }) { Text(message) }
            }

            Button(attrs = {
                onClick { if (!loading && code.isNotBlank()) onSubmit(code) }
                if (loading || code.isBlank()) disabled()
                style {
                    property("margin-top", "16px")
                    width(100.percent); padding(13.px)
                    fontSize(15.px); fontWeight(700)
                    color(Color.white); backgroundColor(Primary)
                    borderRadius(14.px); property("border", "none"); cursor("pointer")
                    if (loading || code.isBlank()) property("opacity", "0.6")
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    property("min-height", "46px")
                }
            }) {
                // A spinner while attaching rather than a made-up "Applying…": every word is ReferralCopy.
                if (loading) {
                    InjectSpinKeyframes()
                    Div(attrs = {
                        style {
                            width(18.px); height(18.px); property("box-sizing", "border-box")
                            property("border", "2px solid rgba(255,255,255,0.4)")
                            property("border-top-color", "#ffffff")
                            borderRadius(50.percent)
                            property("animation", "spin 0.8s linear infinite")
                        }
                    })
                } else {
                    Text(ReferralCopy.DIALOG_APPLY)
                }
            }

            Div(attrs = {
                onClick { if (!loading) onDismiss() }
                style {
                    property("margin-top", "10px"); fontSize(13.px); color(Gray500); cursor("pointer")
                }
            }) { Text(ReferralCopy.DIALOG_CANCEL) }
        }
    }
}


/**
 * The ThriveCart checkout embedded in a full-screen overlay.
 *
 * An iframe rather than a redirect so the buyer stays on the site — ThriveCart serves the page with
 * no X-Frame-Options and only a REPORT-ONLY CSP carrying no frame-ancestors, so framing is
 * permitted. `allow="payment"` is what lets the Stripe card fields inside it work.
 *
 * Closing is only ever the user's choice; the purchase is granted server-side by ThriveCart's
 * webhook, so dismissing early can never lose a payment — the caller re-checks the entitlement.
 */
@Composable
private fun ThriveCartCheckoutFrame(
    url: String,
    /** What the buyer was searching when the paywall interrupted them. Null when there is no
     *  search behind it — then the reminder is simply omitted. Mirrors composeApp's twin. */
    title: String?,
    onDismiss: () -> Unit,
    onSucceeded: () -> Unit = {},
    onSuccessDismissed: () -> Unit = {},
) {
    var loaded by remember(url) { mutableStateOf(false) }
    var paid by remember(url) { mutableStateOf(false) }
    var countdown by remember(url) { mutableStateOf(3) }

    // Reading the frame's location THROWS while it is showing ThriveCart, because that is another
    // origin — and succeeds the moment it redirects to our own thank-you page. So the exception is
    // the signal that payment has not happened yet, and its absence is the signal that it has.
    // There is no load event to use instead: a cross-origin frame fires one without telling us
    // where it went.
    var frameEl by remember(url) { mutableStateOf<org.w3c.dom.HTMLIFrameElement?>(null) }
    LaunchedEffect(url, frameEl) {
        val el = frameEl ?: return@LaunchedEffect
        while (!paid) {
            delay(500)
            val here = try { el.contentWindow?.location?.href } catch (_: Throwable) { null }
            if (PricingConfig.isThriveCartSuccessUrl(here)) {
                paid = true
                onSucceeded()
            }
        }
    }
    LaunchedEffect(paid) {
        if (!paid) return@LaunchedEffect
        while (countdown > 0) { delay(1000); countdown -= 1 }
        onSuccessDismissed()
    }
    // Ellipsis animates on its own clock so it doesn't pulse in lockstep with the fade.
    var dots by remember { mutableStateOf(1) }
    LaunchedEffect(Unit) {
        while (true) { delay(450); dots = if (dots >= 3) 1 else dots + 1 }
    }
    InjectStyles(
        "tc-checkout-loading",
        """
        .tc-checkout-spinner { width: 34px; height: 34px; border-radius: 50%;
            border: 3px solid rgba(13,118,128,0.18); border-top-color: #0D7680;
            animation: tc-checkout-spin 800ms linear infinite; }
        @keyframes tc-checkout-spin { to { transform: rotate(360deg); } }
        /* Slow breathing fade rather than a hard blink — reads as "working", not "stuck". */
        .tc-checkout-pulse { animation: tc-checkout-fade 1800ms ease-in-out infinite; }
        @keyframes tc-checkout-fade { 0%,100% { opacity: .45 } 50% { opacity: 1 } }
        """.trimIndent()
    )
    Div(attrs = {
        style {
            property("position", "fixed"); property("inset", "0")
            property("background", "rgba(0,0,0,0.6)"); property("z-index", "3000")
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            padding(12.px)
        }
    }) {
        Div(attrs = {
            style {
                backgroundColor(Color.white); borderRadius(14.px)
                width(100.percent); property("max-width", "560px"); height(94.percent)
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                property("overflow", "hidden")
                property("box-shadow", "0 20px 60px rgba(0,0,0,0.25)")
            }
        }) {
            // The header IS the reminder — it replaced a "Secure checkout" label that sat above a
            // second band saying this. The page below is a standalone sales page with no idea a
            // search is waiting behind it, which is why the line has to exist; the security label
            // earned less than naming what they were trying to do. Shown even with no title (the
            // plan switcher and the profile's subscribe tap both pass null). Empty once paid, so it
            // never reads "please subscribe" beside "You're in!". Mirrors composeApp's twin.
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                    property("padding", "10px 6px 10px 14px")
                    property("border-bottom", "1px solid #e5e7eb")
                }
            }) {
                Span(attrs = {
                    style {
                        property("flex", "1"); property("padding-right", "8px")
                        fontSize(13.px); color(Gray600); property("line-height", "1.4")
                    }
                }) {
                    if (!paid) {
                        if (!title.isNullOrBlank()) {
                            Text("To search ")
                            // Year dropped for reading, not for identity — the search itself still
                            // carries it. See withoutTrailingYear. Mirrors composeApp's twin.
                            Span(attrs = { style { fontWeight(700); color(Gray900) } }) {
                                Text(title.withoutTrailingYear())
                            }
                            Text(", please subscribe below")
                        } else {
                            Text("Please subscribe below to continue")
                        }
                    }
                }
                Span(attrs = {
                    onClick { onDismiss() }
                    style { fontSize(20.px); cursor("pointer"); property("padding", "0 6px"); color(Gray500) }
                }) { Text("\u2715") }
            }
            Div(attrs = { style { property("position", "relative"); property("flex", "1"); display(DisplayStyle.Flex) } }) {
                Iframe(attrs = {
                    attr("src", url)
                    // Card fields inside the frame are a Stripe iframe of their own; without the
                    // payment permission they render but cannot take a payment.
                    attr("allow", "payment *; clipboard-write")
                    attr("title", "Checkout")
                    // Compose HTML's Iframe has no onLoad attribute, so the listener goes
                    // on the real DOM node.
                    ref { iframe ->
                        frameEl = iframe as? org.w3c.dom.HTMLIFrameElement
                        val handler: (org.w3c.dom.events.Event) -> Unit = { loaded = true }
                        iframe.addEventListener("load", handler)
                        onDispose {
                            iframe.removeEventListener("load", handler)
                            frameEl = null
                        }
                    }
                    style { width(100.percent); property("flex", "1"); property("border", "none") }
                })
                // Covers the blank frame while the offer loads — without it the sheet looks broken
                // for the second or two the page takes to arrive.
                if (!loaded && !paid) {
                    Div(attrs = {
                        style {
                            property("position", "absolute"); property("inset", "0")
                            backgroundColor(Color.white)
                            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                            alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                            property("gap", "16px")
                        }
                    }) {
                        Div(attrs = { classes("tc-checkout-spinner") })
                        // The dots get their own FIXED-WIDTH slot, with a matching empty one on
                        // the left. Appending them to the string instead made the whole line grow,
                        // and a centred line that grows re-centres — so the sentence slid sideways
                        // in time with the animation. Two equal slots hold the row's width
                        // constant, which keeps the sentence dead centre. Mirrors composeApp's twin.
                        Div(attrs = {
                            classes("tc-checkout-pulse")
                            style {
                                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                                fontSize(15.px); fontWeight(500); color(Gray600)
                            }
                        }) {
                            Span(attrs = { style { width(14.px); property("flex", "none") } })
                            Span { Text("Loading the best offer for you") }
                            Span(attrs = {
                                style {
                                    width(14.px); property("flex", "none")
                                    property("text-align", "left")
                                }
                            }) { Text(".".repeat(dots)) }
                        }
                    }
                }
                // Covers the thank-you page's "create an account" prompt, which is wrong for anyone
                // who reached this from inside the app — they were signed in before they ever saw
                // the paywall. Mirrors composeApp's CheckoutSuccessPanel.
                if (paid) {
                    Div(attrs = {
                        style {
                            property("position", "absolute"); property("inset", "0")
                            backgroundColor(Color.white)
                            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                            alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                            property("gap", "10px"); padding(24.px); textAlign("center")
                        }
                    }) {
                        Div(attrs = { style { fontSize(52.px); color(ScoreGreen); fontWeight(700) } }) { Text("\u2713") }
                        Div(attrs = { style { fontSize(24.px); fontWeight(700); color(Gray900) } }) { Text("You're in!") }
                        Div(attrs = { style { fontSize(14.px); color(Gray600); property("max-width", "340px"); property("line-height", "1.5") } }) {
                            Text("Your subscription is active. No need to sign up again \u2014 you're already signed in.")
                        }
                        Div(attrs = { style { fontSize(13.px); color(Gray500); property("margin-top", "8px") } }) {
                            Text(if (countdown > 0) "Taking you back in ${'$'}countdown\u2026" else "Taking you back\u2026")
                        }
                        Div(attrs = {
                            onClick { onSuccessDismissed() }
                            style { fontSize(14.px); fontWeight(700); color(Primary); cursor("pointer"); property("margin-top", "6px") }
                        }) { Text("Continue now") }
                    }
                }
            }
        }
    }
}
